import { useToast } from "@/components/ui/use-toast";
import { supabaseClient } from "@/lib/supabase";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { consentKeys } from "./selectConsentById";

import type { ConsentSignatureT } from "@/types/database/consents";
import useInvalidateDiaries from "../../diaries/features/edit/api/invalidateDiaries";

interface SignConsentParams {
	consent_id: string;
	user_id: string;
	signed_at?: string;
	ip_address?: string;
	diary_id?: string;
}

/**
 * Updates a consent signature in the database or creates a new one if it doesn't exist
 */
async function signConsent(
	params: SignConsentParams,
): Promise<ConsentSignatureT> {
	const { consent_id, user_id } = params;

	if (!consent_id || !user_id) {
		throw new Error(
			"Missing required fields: consent_id and user_id are required",
		);
	}

	// Prepare data for upsert - exclude diary_id as it's not a column in the table
	const { diary_id, ...dataToUpsert } = params;

	// Add signed_at if not provided
	if (!dataToUpsert.signed_at) {
		dataToUpsert.signed_at = new Date().toISOString();
	}

	try {
		const { data, error } = await supabaseClient
			.from("consent_signatures")
			.upsert(dataToUpsert, {
				onConflict: "consent_id,user_id",
				ignoreDuplicates: false,
			})
			.select()
			.single();

		if (error) {
			throw new Error(error.message);
		}

		return data as ConsentSignatureT;
	} catch (error: unknown) {
		// If it's a duplicate key error, fetch the existing record
		const errorMessage = error instanceof Error ? error.message : String(error);

		if (errorMessage.includes("duplicate key value")) {
			const { data, error: fetchError } = await supabaseClient
				.from("consent_signatures")
				.select("*")
				.eq("consent_id", consent_id)
				.eq("user_id", user_id)
				.single();

			if (fetchError) {
				throw new Error(
					`Error fetching existing signature: ${fetchError.message}`,
				);
			}

			return data as ConsentSignatureT;
		}
		throw error;
	}
}

/**
 * Hook for managing consent signature mutations
 */
export function useSignConsentMutation(options?: {
	onSuccess?: (data: ConsentSignatureT, variables: SignConsentParams) => void;
}) {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const invalidateDiaries = useInvalidateDiaries();
	return useMutation({
		mutationFn: signConsent,
		onSuccess: (data, variables) => {
			// Invalidate the specific consent query to refresh the data
			if (variables.consent_id) {
				queryClient.invalidateQueries({
					queryKey: consentKeys.byId(variables.consent_id),
				});
			}

			// Invalidate the diary's consents if diary_id is provided
			if (variables.diary_id) {
				queryClient.invalidateQueries({
					queryKey: consentKeys.byDiaryId(variables.diary_id),
				});
			}

			// Show success toast
			toast({
				title: "Consent signed successfully",
				description: "Thank you for signing the consent form",
			});

			// Call the onSuccess callback if provided
			if (options?.onSuccess) {
				options.onSuccess(data, variables);
			}

			// Invalidate all diary queries to refresh the consent status
			invalidateDiaries();
		},
		onError: (error) => {
			// Show error toast
			toast({
				title: "Error signing consent",
				description: error instanceof Error ? error.message : "Unknown error",
				variant: "destructive",
			});
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: consentKeys.all,
			});
			// Invalidate all diary queries to ensure everything is up to date
			invalidateDiaries();
		},
	});
}
